/* eslint-disable max-len */
import { Burden } from '@deqo/foundation';
import { shade } from 'polished';
import React, { ReactNode, UIEventHandler, useCallback, useContext } from 'react';
import SplitPane from 'react-split-pane';
import styled, { css, keyframes } from 'styled-components';
import { Link } from 'wouter';
import { Images } from '../../@resources/img';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { Icon } from '../../concepts/resources/Icon';
import { Store, StoreWithUpdate } from '../../concepts/revive/Store';
import { LocalStorageService, StorageService } from '../../platform/StorageService';
import { DropButton } from '../../ui/DropButton';
import { Fill } from '../../ui/Fill';
import { ContainerScope, resolve } from '../Container';
import { XmasHat } from '../occasions/xmas/XmasHat';
import { Colors, remInPx } from './../../ui';

const XSplitPane = SplitPane as any;

const themeBg = Colors.primary;
const themeFg = '#fff';

const leftSideIn = keyframes`
    from { transform: translateX(-4rem); opacity: 0; }
    50% { transform: translateX(-4rem); opacity: 0; }
    to { transform: translateX(0rem); opacity: 1; }
`;

const shouldAnimate = (() => {
    // Oof
    const enabled = !location.pathname.startsWith('/BO');
    const st = {} as Record<string, number>;
    return (key: string) => {
        const start = (st[key] ?? (st[key] = performance.now()));
        return enabled && (performance.now() - start) < 1000;
    };
})();

/* --------------------------------- Context -------------------------------- */

type PageStore = StoreWithUpdate<{ leftSize: number, leftOpen: boolean, leftPadding: number }>;
const PageContext = React.createContext(null as PageStore);


/* --------------------- Title, Header, Aside & Content --------------------- */

const Title = styled.div`
    margin: 0 2.5rem;
    font-size: 1.25rem;    
    font-weight: 400;
    color: ${Colors.primaryText};
`;

type HeaderDivide = 'default' | 'hover' | 'off';

const HeaderFrame = styled.div<{ divide?: HeaderDivide }>`
    position: relative;
    z-index: 2;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 4rem;
    background: white;
    box-shadow: ${p => p.divide === 'hover'
        ? '0 1px 5px -1px rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.06)'
        // ? Shadows.sm
        : (p.divide === 'off' ? 'none' : '0 2px 0 0 #eeeeee')};
    /* border-bottom: solid 1px #F0F2F5; */
`;

const HeaderCenter = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    & > * {
        pointer-events: all;
    }
`;

const HeaderPush = styled.div`
    display: flex;
    align-items: center;
    margin-left: auto;
`;

const HeaderBlock = styled.div<{ push?: boolean }>`
    display: flex;
    align-items: center;
    margin: 0 .75rem;
`;

const Header: LFC<{ divide?: HeaderDivide }> = ({ divide, children }) => {

    const { leftOpen, leftPadding } = useObserver(useContext(PageContext));
    const paddingLeft = `${leftOpen ? 0 : leftPadding}rem`;

    return <HeaderFrame divide={divide} style={{ paddingLeft }}>
        {children}
    </HeaderFrame>;
};

/*
before: {
        opacity: 0,
        scale: .5,
    },
    in: {
        opacity: 1,
        scale: 1.0,
        transition: {
            opacity: { duration: .3, ease: 'linear', },
            scale: { duration: .4, ease: [0.22, 1, 0.36, 1], },
        }
    }
*/

const contentInFade = keyframes` from { opacity: 0 } to { opacity: 1 } `;
const contentInSlide = keyframes` from { transform: translateY(10rem) } to { transform: translateY(0rem) } `;

const ContentFrame = styled.div<{ animate?: boolean, background?: string, flush?: boolean }>`
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 1;
    flex: 1 1 auto;
    position: relative;
    padding: ${p => p.flush ? '0' : '2rem 2.5rem'};
    overflow: auto;
    background: ${p => p.background ?? '#fff'};

    ${p => !p.animate ? '' : css`
        animation: 
            ${contentInFade} 300ms linear,
            ${contentInSlide} 400ms cubic-bezier(0.22, 1, 0.36, 1)
        ;
    `}
`;

type ContentProps = { background?: string, flush?: boolean, onScroll?: UIEventHandler<HTMLDivElement>, children?: ReactNode };

const Content = React.memo<ContentProps>(({ children, ...rest }) =>
    <ContentFrame animate={shouldAnimate('content')} {...rest}>
        {children}
    </ContentFrame>
);

const Flex = styled.div`
    position: relative;
    z-index: 1;
    flex: 1 1 auto;
`;

const Aside = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    flex: 1 1 auto;
    border-left: solid .125rem #eee;
`;

const Split: LFC = ({ children }) =>
    <XSplitPane className="flex" split="vertical" primary="second" minSize={remInPx(10)} size={300} maxSize={remInPx(30)}>
        {children}
    </XSplitPane>;

/* --------------------------------- Canton --------------------------------- */

const CantonLogo = styled.div`
    position: relative;
    display: flex;
    justify-content: flex-start;
    width: 4.6rem;
    height: 4rem;
    padding: 0.25rem 1rem 0.25rem 0.5rem;
    margin-right: 0.3rem;
    box-sizing: border-box;
    overflow: hidden;

    &:before {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        width: 4.6rem;
        height: 6rem;
        transform: translate(-50%, -50%) translateX(-1px);
        background: ${Colors.primary};
        border-radius: 0 6rem 6rem 0;
    }

    &:after {
        content: '';
        position: relative;
        width: 4rem;
        left: -0.1rem;
        background: url(${Images.LogoOutline}) center center no-repeat;
        background-size: 2.75rem auto;
    }

    &:hover {
        cursor: pointer;
        &:before { background: ${shade(0.1, themeBg)}; }
    }

    &:active {
        &:before { background: ${shade(0.2, themeBg)}; }
    }
`;

const CantonFrame = styled.div<{ animate?: boolean, flip?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    position: absolute;
    z-index: 3;
    top: 0;
    left: 0;
    height: 4rem;

    ${p => p.animate ? css`animation: ${leftSideIn} 500ms cubic-bezier(0,.76,.47,1);` : ''}

    ${p => p.flip ? css`
        background: ${themeFg};
        color: ${Colors.primaryText};
        ${DropButton} {
            color: ${Colors.primaryText};
            margin-left: .25rem;
            margin-right: .25rem;
        }
    ` : css`
        background: ${themeBg};
        color: ${themeFg};
        ${DropButton} {
            color: #fff;
            margin-right: .25rem;
            &:hover {
                color: ${Colors.primaryText};
            }
        }
    `}

    &:hover ${CantonLogo} {
        &:before {
            display: ${p => p.flip ? 'block' : 'none'};
        }
    }

    & > a {
        display: flex;
        align-items: center;
    }
`;

const CantonLabel = styled.div`
    margin-left: -0.5rem;
    color: ${themeFg};
    font-family: 'Rubik';
    font-weight: 450;
    font-size: 1.6rem;
`;

// const CantonEdge = styled.div`
//     position: absolute;
//     top: 0;
//     right: 0px;
//     bottom: 0;
//     background: #eee;
//     width: 2px;
// `;

const CantonBend = styled.div`
    position: absolute;
    top: 0;
    right: -1rem;
    display: flex;
    justify-content: center;
    width: 1.8rem;
    height: 4rem;
    box-sizing: border-box;
    overflow: hidden;

    &:before {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        width: 3rem;
        height: 6rem;
        transform: translate(-100%, -50%);
        border: solid 1px #eee;
        border-radius: 0 6rem 6rem 0;
    }
`;

const CantonPush = styled.div`
    display: flex;
    align-items: center;
    margin-left: auto;
    margin-right: .5rem;
`;

const Canton: LFC<{ href?: string, navigation?: boolean, menu?: boolean }> = ({ href, navigation, menu }) => {

    const store = useContext(PageContext);
    const { leftOpen, leftSize, leftPadding } = useObserver(store);
    const toggleLeftOpen = useCallback(() => store.update({ leftOpen: !leftOpen }), [leftOpen]);
    const width = `${leftOpen ? leftSize : remInPx(leftPadding)}px`;

    return <CantonFrame id="canton" animate={shouldAnimate('leftSize')} flip={!leftOpen} style={{ width }}>
        <Link to={href ?? '/'}>
            <CantonLogo />
            <XmasHat />
            {leftOpen && <CantonLabel>Ludi</CantonLabel>}
        </Link>
        {navigation && leftOpen && <>
            <CantonPush>
                <DropButton rounding="full" padding=".375" onClick={toggleLeftOpen}>
                    <Icon size="1.375" src="deqo://icons/hero/chevron-left" />
                </DropButton>
            </CantonPush>
        </>}
        {navigation && !leftOpen && <>
            <DropButton rounding="full" padding=".375" onClick={toggleLeftOpen}>
                <Icon size="1.5" src="deqo://icons/hero/chevron-right" />
            </DropButton>
            <CantonBend />
        </>}
        {menu && <>
            <DropButton rounding="full" padding=".375" onClick={toggleLeftOpen}>
                <Icon size="1.5" src="deqo://icons/hero/menu" />
            </DropButton>
            <CantonBend />
        </>}
    </CantonFrame>;
};


/* ------------------------------- Navigation ------------------------------- */

const NavigationFrame = styled.div<{ animate: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 4;
    pointer-events: none;

    ${p => p.animate ? css`animation: ${leftSideIn} 500ms cubic-bezier(0,.76,.47,1);` : ''}
`;

const NavigationMain = styled(Fill)`
    /* background: ${Colors.primary}; */
    top: 4rem;
    color: ${themeFg};
    background: ${themeBg};
    box-shadow: 0px 3px 6px -1px rgba(0,0,0,0.1), 0 3px 4px -1px rgba(0,0,0,0.06);
    /* border-top: solid 1px #eee; */

    ${DropButton} {
        color: #fff;
    }
`;


const Navigation: LFC = ({ children }) => {

    const store = useContext(PageContext);
    const { leftOpen, leftSize } = useObserver(store);
    const setLeftSize = useCallback((v: number) => store.update({ leftSize: v }), [store]);

    if (!leftOpen) return null;

    return <NavigationFrame animate={shouldAnimate('leftSize')}>
        <XSplitPane split="vertical" minSize={remInPx(22)} size={leftSize} maxSize={remInPx(40)} onChange={setLeftSize}>
            <NavigationMain id="navigation">
                {children}
            </NavigationMain>
            <div></div>
        </XSplitPane>
    </NavigationFrame>;
};


/* -------------------------------- Children -------------------------------- */

const ChildrenFrame = styled(Fill)`
    display: flex;
    flex-direction: column;
`;

const Children: LFC = ({ children }) => {

    const store = useContext(PageContext);
    const { leftOpen, leftSize } = useObserver(store);
    const push = leftOpen ? leftSize : 0;

    return <ChildrenFrame style={{ left: `${push}px` }}>
        {children}
    </ChildrenFrame>;
};


/* ---------------------------------- Base ---------------------------------- */

const BaseFrame = styled(Fill)`
    background: #fff;    
`;

export type PageProps = { leftPolicy?: [string, boolean, number], canton?: string, navigation?: React.ComponentType, menu?: React.ComponentType, children?: ReactNode };

class Base extends React.Component<PageProps> {

    public static contextType = ContainerScope;

    @resolve(LocalStorageService) private localData!: StorageService;

    private store = null as PageStore;
    private obsvr = null as Burden;

    public render() {
        if (!this.store) return null;
        const NavImpl = this.props.navigation;
        const MenuImpl = this.props.menu;
        return <PageContext.Provider value={this.store}>
            <BaseFrame>
                <Children>
                    {this.props.children}
                </Children>
                {!!NavImpl &&
                    <Navigation>
                        <NavImpl />
                    </Navigation>
                }
                {!!this.props.canton &&
                    <Canton href={this.props.canton} navigation={!!this.props.navigation} menu={!!this.props.menu} />
                }
            </BaseFrame>
        </PageContext.Provider>;
    }

    public componentDidMount() {
        const leftPolicy = this.props.leftPolicy ?? ['default', true, 22];
        const openKey = `deqo/ui/nav/lopen.${leftPolicy[0]}`;
        const sizeKey = 'deqo/ui/nav/lsize';
        const leftOpen = (this.localData.get(openKey) ?? leftPolicy[1]) && !!this.props.navigation;
        const leftSize = this.localData.get(sizeKey) ?? remInPx(leftPolicy[2]);
        this.store = Store.create({ leftSize, leftOpen, leftPadding: !!(this.props.navigation || this.props.menu) ? 8.2 : 4.2 });
        this.obsvr = this.store.observe(ns => {
            this.localData.put(sizeKey, ns.leftSize);
            this.localData.put(openKey, ns.leftOpen);
        });
        this.forceUpdate();
    }

    public componentWillUnmount() {
        Burden.resolve(this.obsvr);
    }
}


/* --------------------------------- Export --------------------------------- */

export const Page = Object.assign(Base, {
    Aside,
    Content,
    Split,
    Flex,
    Header,
    HeaderBlock,
    HeaderCenter,
    HeaderPush,
    Navigation,
    Title,
});
